import React, { useState, useEffect } from 'react';
import { MenuItem, Select, IconButton, InputLabel, FormControl, Box, Button, TextField, CircularProgress, FormControlLabel, Checkbox } from "@mui/material";
import { Formik, FieldArray } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import Header from "../../../components/Header";
import axios from 'axios';
import { useParams, useNavigate } from "react-router-dom";
import { Remove } from "@mui/icons-material";

const EditCliente = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { id } = useParams();
  const [colaboradores, setColaboradores] = useState([]);
  const [initialValues, setInitialValues] = useState(null);

  const fetchClienteData = async () => {
    try {
      const response = await axios.get(`https://pf-auditores.ew.r.appspot.com/api/clientes/${id}`);
      const clienteData = response.data;
      setInitialValues({
        numCliente: clienteData.numCliente || "",
        grupoI: clienteData.grupoI || "",
        grupoII: clienteData.grupoII || "",
        nif: clienteData.nif || "",
        nomeCliente: clienteData.nomeCliente || "",
        vinculo: clienteData.vinculo || "",
        periodo: clienteData.periodo || "",
        socio: clienteData.socio || "",
        aprovador1: clienteData.aprovador1 || "",
        aprovador2: clienteData.aprovador2 || "",
        faturado: clienteData.faturado || false,
        mandatos: clienteData.mandatos.map(mandato => ({
          ano: mandato.ano || "",
          honorario: mandato.honorario || "",
        })),
      });
    } catch (error) {
      console.error("Erro ao buscar detalhes do cliente:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClienteData();
    const fetchColaboradores = async () => {
      try {
        const response = await axios.get('https://pf-auditores.ew.r.appspot.com/api/colaboradores');
        setColaboradores(response.data);
      } catch (error) {
        console.error('Erro ao buscar colaboradores:', error);
      }
    };

    fetchColaboradores();
  }, [id]);

  const handleFormSubmit = async (values, actions) => {
    const payload = {
      numCliente: values.numCliente,
      grupoI: values.grupoI,
      grupoII: values.grupoII,
      nif: values.nif,
      nomeCliente: values.nomeCliente,
      vinculo: values.vinculo,
      periodo: values.periodo,
      mandatos: values.mandatos.map(mandato => ({
        ano: mandato.ano,
        honorario: parseFloat(mandato.honorario), // Converter para número
      })),
      socio: values.socio,
      aprovador1: values.aprovador1,
      aprovador2: values.aprovador2,
      faturado: values.faturado,
    };
    console.log("Payload:", payload); // Verificar o payload
    setLoading(true);
    try {
      const response = await axios.put(`https://pf-auditores.ew.r.appspot.com/api/clientes/${id}`, payload);
      console.log("Response:", response.data); // Verificar a resposta da API
      actions.resetForm();
      navigate("/clientes");
    } catch (error) {
      console.error("Erro ao editar cliente:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!initialValues) {
    return null; // Ou um indicador de carregamento adicional se necessário
  }

  // Função para obter o primeiro e último nome
  const getFirstAndLastName = (fullName) => {
    const nomeAbreviado = fullName.split(' ');
    return `${nomeAbreviado[0]} ${nomeAbreviado[nomeAbreviado.length - 1]}`;
  };

  return (
    <Box m="20px">
      <Header title="Editar Cliente" subtitle="Edite os detalhes do cliente" />
      
      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={checkoutSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              <TextField
                label="Número do Cliente"
                name="numCliente"
                value={values.numCliente}
                onChange={handleChange}
                fullWidth
                variant="filled"
                error={!!touched.numCliente && !!errors.numCliente}
                helperText={touched.numCliente && errors.numCliente}
                sx={{ gridColumn: "span 1" }}
              />
              <TextField
                label="Grupo I"
                name="grupoI"
                value={values.grupoI}
                onChange={handleChange}
                fullWidth
                variant="filled"
                error={!!touched.grupoI && !!errors.grupoI}
                helperText={touched.grupoI && errors.grupoI}
                sx={{ gridColumn: "span 1" }}
              />
              <TextField
                label="Grupo II"
                name="grupoII"
                value={values.grupoII}
                onChange={handleChange}
                fullWidth
                variant="filled"
                error={!!touched.grupoII && !!errors.grupoII}
                helperText={touched.grupoII && errors.grupoII}
                sx={{ gridColumn: "span 1" }}
              />
              <TextField
                label="NIF"
                name="nif"
                value={values.nif}
                onChange={handleChange}
                fullWidth
                variant="filled"
                error={!!touched.nif && !!errors.nif}
                helperText={touched.nif && errors.nif}
                sx={{ gridColumn: "span 1" }}
              />
              <TextField
                label="Nome do Cliente"
                name="nomeCliente"
                value={values.nomeCliente}
                onChange={handleChange}
                fullWidth
                variant="filled"
                error={!!touched.nomeCliente && !!errors.nomeCliente}
                helperText={touched.nomeCliente && errors.nomeCliente}
                sx={{ gridColumn: "span 1" }}
              />
              <FormControl variant="filled" fullWidth sx={{ gridColumn: "span 1" }}>
                <InputLabel>Aprovador 1</InputLabel>
                <Select
                  name="aprovador1"
                  value={values.aprovador1}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={!!touched.aprovador1 && !!errors.aprovador1}
                >
                  {colaboradores
                    .filter(colaborador => colaborador.tipo === 'ADMIN')
                    .map(colaborador => (
                      <MenuItem key={colaborador.id} value={colaborador.id}>
                        {getFirstAndLastName(colaborador.nome)}
                      </MenuItem>
                    ))}
                </Select>
                {touched.aprovador1 && errors.aprovador1 && (
                  <div style={{ color: 'red', marginTop: 8 }}>{errors.aprovador1}</div>
                )}
              </FormControl>
              <FormControl variant="filled" fullWidth sx={{ gridColumn: "span 1" }}>
                <InputLabel>Aprovador 2</InputLabel>
                <Select
                  name="aprovador2"
                  value={values.aprovador2}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={!!touched.aprovador2 && !!errors.aprovador2}
                >
                  {colaboradores
                    .filter(colaborador => colaborador.tipo === 'ADMIN')
                    .map(colaborador => (
                      <MenuItem key={colaborador.id} value={colaborador.id}>
                        {getFirstAndLastName(colaborador.nome)}
                      </MenuItem>
                    ))}
                </Select>
                {touched.aprovador2 && errors.aprovador2 && (
                  <div style={{ color: 'red', marginTop: 8 }}>{errors.aprovador2}</div>
                )}
              </FormControl>
              <FormControl variant="filled" fullWidth sx={{ gridColumn: "span 1" }}>
                <InputLabel>Vínculo</InputLabel>
                <Select
                  name="vinculo"
                  value={values.vinculo}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={!!touched.vinculo && !!errors.vinculo}
                >
                  <MenuItem value="Consultoria">Consultoria</MenuItem>
                  <MenuItem value="Auditoria">Auditoria</MenuItem>
                </Select>
                {touched.vinculo && errors.vinculo && (
                  <div style={{ color: 'red', marginTop: 8 }}>{errors.vinculo}</div>
                )}
              </FormControl>
              <TextField
                label="Sócio"
                name="socio"
                value={values.socio}
                onChange={handleChange}
                fullWidth
                variant="filled"
                error={!!touched.socio && !!errors.socio}
                helperText={touched.socio && errors.socio}
                sx={{ gridColumn: "span 1" }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.faturado}
                    onChange={handleChange}
                    name="faturado"
                  />
                }
                label="Faturado"
                sx={{ gridColumn: "span 1" }}
              />
              <TextField
                label="Mandato"
                name="periodo"
                value={values.periodo}
                onChange={handleChange}
                fullWidth
                variant="filled"
                error={!!touched.periodo && !!errors.periodo}
                helperText={touched.periodo && errors.periodo}
                sx={{ gridColumn: "span 1" }}
              />
              
              <FieldArray name="mandatos">
                {({ push, remove }) => (
                  <Box sx={{ gridColumn: "span 4" }}>
                    {values.mandatos.map((mandato, index) => (
                      <Box key={index} display="flex" alignItems="center" gap="10px" mb="10px">
                        <TextField
                          name={`mandatos[${index}].ano`}
                          label="Ano"
                          value={mandato.ano}
                          onChange={handleChange}
                          fullWidth
                          variant="filled"
                          error={!!touched.mandatos?.[index]?.ano && !!errors.mandatos?.[index]?.ano}
                          helperText={touched.mandatos?.[index]?.ano && errors.mandatos?.[index]?.ano}
                        />
                        <TextField
                          name={`mandatos[${index}].honorario`}
                          label="Honorários"
                          value={mandato.honorario}
                          onChange={handleChange}
                          fullWidth
                          variant="filled"
                          error={!!touched.mandatos?.[index]?.honorario && !!errors.mandatos?.[index]?.honorario}
                          helperText={touched.mandatos?.[index]?.honorario && errors.mandatos?.[index]?.honorario}
                        />
                        <IconButton onClick={() => remove(index)}>
                          <Remove />
                        </IconButton>
                      </Box>
                    ))}
                    <Button variant="contained" onClick={() => push({ ano: '', honorario: '' })}>
                      Adicionar Mandato
                    </Button>
                  </Box>
                )}
              </FieldArray>
            </Box>
            <Box display="flex" justifyContent="space-between" mt="20px">
              <Button variant="contained" color="primary" onClick={() => navigate("/clientes")}>
                Voltar
              </Button>
              <Button type="submit" color="secondary" variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Salvar Alterações'}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

const checkoutSchema = yup.object().shape({
  numCliente: yup.string().required("Requerido"),
  grupoI: yup.string().required("Requerido"),
  grupoII: yup.string().required("Requerido"),
  nif: yup.string().required("Requerido"),
  nomeCliente: yup.string().required("Requerido"),
  vinculo: yup.string().required("Requerido"),
  periodo: yup.string().required("Requerido"),
  socio: yup.string().required("Requerido"),
  aprovador1: yup.string().required("Requerido"),
  aprovador2: yup.string().required("Requerido"),
  faturado: yup.boolean(),
  mandatos: yup.array().of(
    yup.object().shape({
      ano: yup.string().required("Requerido"),
      honorario: yup.string().required("Requerido"),
    })
  ),
});

export default EditCliente;
